import React, { useState } from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
	Settings,
	HelpCircle,
	FileBarChart,
	UserCircle,
	Shield,
	User,
	Home,
	Info,
	Menu as MenuIcon,
	Hammer,
	Banknote,
	ListChecks,
	Building2,
	Infinity as ProvisionIcon,
	Globe,
	BookUser,
	ScrollText,
	Users,
	BookOpenCheck,
	FileChartColumn,
	FileText,
	PackagePlus,
	Lock
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Triquetra from './icons/triquetra';
import { useFeatures } from '../providers/features-provider';
import { usePermissions } from '../providers/permissions-provider';
import menuData from '../files/json/sidebar-menu.json';
import adminMenuData from '../files/json/admin-sidebar-menu.json';

// Import logo.png
import logoImage from '../files/images/logo.png';
import logoDarkImage from '../files/images/logo-dark.png';

const iconComponentMap: Record<string, React.ElementType> = {
	dashboard: ProvisionIcon,
	transaction: Triquetra,
	settings: Settings,
	help: HelpCircle,
	reports: FileBarChart,
	profile: UserCircle,
	users: Users,
	account: User,
	security: Shield,
	home: Home,
	info: Info,
	build: Hammer,
	provision: Banknote,
	category: ListChecks,
	taxdefinition: FileBarChart,
	bankdefintion: Banknote,
	company: Building2,
	country: Globe,
	receipt: ScrollText,
	client: BookUser,
	bookOpenCheck: BookOpenCheck,
	fileChartColumn: FileChartColumn,
	document: FileText,
	addon: PackagePlus,
};

type MenuItem = {
	label: string;
	icon?: string;
	path?: string;
	featureKey?: string;
	// A plan feature (e.g. Documents) - shown greyed out with a lock when the
	// company's plan doesn't have it, instead of hidden.
	planGated?: boolean;
	locked?: boolean;
	resourceKey?: string;
	children?: MenuItem[];
};

interface SidebarMobileMenuProps {
	onNavigate?: () => void;
}

const SidebarMobileMenu: React.FC<SidebarMobileMenuProps> = ({ onNavigate }) => {
	const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
	const navigate = useNavigate();
	const location = useLocation();
	const { isVisibleInNav } = useFeatures();
	const { can } = usePermissions();

	const isAdmin = location.pathname.includes('admin');

	// Same filtering the desktop sidebar (sidebar.tsx) applies - without this,
	// mobile showed every nav item regardless of feature flags or the user's
	// actual read permission on that resource.
	const filterByFeature = (items: MenuItem[]): MenuItem[] =>
		items
			.filter(item => !item.featureKey || item.planGated || isVisibleInNav(item.featureKey))
			.map(item => (item.planGated && item.featureKey && !isVisibleInNav(item.featureKey) ? { ...item, locked: true } : item))
			.map(item => item.children
				? { ...item, children: filterByFeature(item.children) }
				: item
			);

	const filterByPermission = (items: MenuItem[]): MenuItem[] =>
		items
			.map(item => item.children
				? { ...item, children: filterByPermission(item.children) }
				: item
			)
			.filter(item => {
				if (item.children) return item.children.length > 0;
				if (!item.resourceKey) return true;
				return can(item.resourceKey, 'read');
			});

	const menuItems = isAdmin
		? filterByFeature(adminMenuData as MenuItem[])
		: filterByPermission(filterByFeature(menuData as MenuItem[]));

	const handleToggle = (label: string) => {
		setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
	};

	const handleNavigate = (path: string) => {
		navigate(path);
		onNavigate?.();
	};

	const renderMenuItems = (items: MenuItem[], level = 0) => {
		return items.map((item, index) => {
			const IconComponent = item.icon && iconComponentMap[item.icon] ? iconComponentMap[item.icon] : MenuIcon;
			const hasChildren = item.children && item.children.length > 0;
			const isOpen = openMenus[item.label] || false;
			const isActive = item.path && location.pathname === item.path;
			const iconColor = isActive ? 'var(--color-primary)' : 'var(--color-text-muted)';

			return (
				<React.Fragment key={`${level}-${index}`}>
					<ListItemButton
						onClick={() => {
							if (hasChildren) {
								handleToggle(item.label);
							} else if (item.path) {
								handleNavigate(item.path);
							}
						}}
						sx={{
							pl: 2 + level * 2,
							backgroundColor: isActive ? 'var(--color-container-background)' : 'transparent',
                            color: isActive ? 'primary.main' : 'text.primary',
							opacity: item.locked && !isActive ? 0.5 : 1,
							'&:hover': {
								backgroundColor: 'var(--color-container-background)',
							},
						}}
					>
						{IconComponent && (
							<ListItemIcon sx={{ minWidth: 40 }}>
								<IconComponent size={20} color={iconColor} />
							</ListItemIcon>
						)}
						<ListItemText 
							primary={item.label}
							primaryTypographyProps={{
								sx: { fontSize: '14px', fontWeight: 500 }
							}}
						/>
						{item.locked && <Lock size={14} />}
						{hasChildren && (
							isOpen ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />
						)}
					</ListItemButton>
					{hasChildren && (
						<Collapse in={isOpen} timeout="auto" unmountOnExit>
							<List component="div" disablePadding>
								{renderMenuItems(item.children || [], level + 1)}
							</List>
						</Collapse>
					)}
				</React.Fragment>
			);
		});
	};

	return (
		<Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 0.5, mb: 1}}>
                <img 
                    src={isAdmin ? logoDarkImage : logoImage} 
                    alt="Logo" 
                    style={{ width: 128, height: 32, padding: '0 12px' }} 
                    onClick={() => {
                        if (location.pathname === '/transaction') {
                            window.location.reload();
                        } else {
                            !isAdmin && navigate('/transaction');
                        }
                    }} 
                />
            </Box>
			<List sx={{ width: '100%', py: 0 }}>
				{renderMenuItems(menuItems)}
			</List>
		</Box>
	);
};

export default SidebarMobileMenu;
